/**
 * Front-end helpers for the vertical PACKAGE catalog (Comms / Field / Unified /
 * Enterprise). Packages are the real product a tenant is on — they carry the
 * price and drive features. The catalog (incl. per-rep price) is served by the
 * admin-service GET /v1/packages and fetched once per session via usePacks().
 *
 * The legacy plan TIER still exists under the hood (it buckets usage limits),
 * but operators only ever see packages now.
 */
import { useEffect, useMemo, useState } from "react";
import { fetchPackages, type PackOption } from "./api";
import type { Tenant } from "../types";

export type PackGroup = PackOption["group"];

export const PACK_GROUP_LABELS: Record<PackGroup, string> = {
  comms: "Comms",
  field: "Field",
  unified: "Combined",
  commerce: "Commerce",
  enterprise: "Enterprise",
  client: "Reseller client"
};

export const PACK_GROUP_ORDER: PackGroup[] = ["comms", "field", "unified", "commerce", "enterprise", "client"];

/**
 * The pack a reseller's client workspace runs on (tenant plan phase 9). Never
 * sold and never billed: Orbit invoices the reseller.
 */
export function isClientPack(p: Pick<PackOption, "group"> | null | undefined): boolean {
  return p?.group === "client";
}

/** "R599 / rep / mo" or "Custom" for quote-based packs. */
export function formatPerRep(cents: number | null | undefined): string {
  if (cents == null) return "Custom";
  return `R${(cents / 100).toLocaleString("en-ZA", { maximumFractionDigits: 0 })} / rep / mo`;
}

/**
 * What this package costs, in the way it is actually charged. A flat-priced
 * pack (Orbit OS) is billed once for the whole business no matter how many
 * users, so showing it as a per-rep number is not a formatting nicety - it is
 * the difference between R2,000 and R2,000 x reps on the invoice.
 */
export function formatPackPrice(p: Pick<PackOption, "perRepMonthlyCents" | "flatMonthlyCents"> & { group?: PackOption["group"] }): string {
  if (p.group === "client") return "Not billed: Orbit invoices the reseller";
  if (p.flatMonthlyCents != null) {
    return `R${(p.flatMonthlyCents / 100).toLocaleString("en-ZA", { maximumFractionDigits: 0 })} / mo flat`;
  }
  return formatPerRep(p.perRepMonthlyCents);
}

/** True for the packages we still sell (the marketing-site line-up). */
export function isSellable(p: PackOption): boolean {
  return p.selfServe === true || p.pack === "orbit-os" || p.group === "enterprise";
}

/**
 * Human labels for the tenant feature flags, in the order an operator reads
 * them. Anything not listed is a flag we do not want on a package card.
 */
const FLAG_LABELS: [string, string][] = [
  ["FEATURE_VOICE", "Orbit number + call routing"],
  ["FEATURE_CALL_TRACKING", "Call tracking"],
  ["FEATURE_CALL_RECORDING", "Call recording + transcripts"],
  ["FEATURE_WHATSAPP", "WhatsApp"],
  ["FEATURE_AI_ASSIST", "AI copilot"],
  ["FEATURE_CRM_PIPELINE", "CRM pipeline"],
  ["FEATURE_COMMERCE", "Quotes, orders + invoicing"],
  ["FEATURE_ACTIVITY_FORMS", "Field forms"],
  ["FEATURE_ACTIVITY_LOG", "Activity history"],
  ["FEATURE_CALL_PLANNING", "Visit planning"],
  ["FEATURE_LIVE_LOCATION", "Live map"],
  ["FEATURE_LOCATION_HISTORY", "Day replay"],
  ["FEATURE_DASHBOARDS", "Dashboards"],
  ["FEATURE_REPORTS", "Reports"],
  ["FEATURE_AUTOMATION", "Automations"],
  ["FEATURE_MARKETING", "Marketing agent"],
  ["FEATURE_COMMERCE_PAYLINKS", "Pay links"]
];

/**
 * The plain-English list of what a package unlocks, straight off the real flag
 * mapping the tenant will be given. Empty when config-service did not answer -
 * render nothing rather than guessing, because a wrong "includes" line on the
 * picker is how a customer ends up sold something they cannot reach.
 */
export function packIncludes(p: PackOption): string[] {
  if (!p.flags) return [];
  return FLAG_LABELS.filter(([k]) => p.flags?.[k] === "true").map(([, label]) => label);
}

/**
 * The notable things this package deliberately does NOT include. Only the ones
 * an operator gets asked about on a sales call - no point listing all sixteen.
 */
const NOTABLE_EXCLUSIONS: [string, string][] = [
  ["FEATURE_VOICE", "no Orbit number or call forwarding"],
  ["FEATURE_CALL_RECORDING", "no call recording"],
  ["FEATURE_WHATSAPP", "no WhatsApp"],
  ["FEATURE_LIVE_LOCATION", "no live map"]
];

export function packExcludes(p: PackOption): string[] {
  if (!p.flags) return [];
  return NOTABLE_EXCLUSIONS.filter(([k]) => p.flags?.[k] !== "true").map(([, label]) => label);
}

/** "R1,199.00" — plain Rand from cents. */
export function formatRand(cents: number | null | undefined): string {
  if (cents == null) return "—";
  return "R" + (cents / 100).toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * A tenant's effective monthly charge in ZAR cents: the locked custom amount if
 * set, otherwise the pack's per-rep price × billed reps. Mirrors the backend
 * MRR logic so console figures line up with what's actually collected.
 */
export function tenantMonthlyCents(
  tenant: Tenant,
  perRepCents: number | null | undefined,
  flatCents?: number | null
): number {
  const locked = tenant.billing?.monthlyAmountCents;
  if (locked && locked > 0) return locked;
  // A flat-priced pack (Orbit OS) is one price for the whole business however
  // many users it has, so it is returned as-is and never multiplied.
  if (flatCents != null && flatCents > 0) return flatCents;
  if (perRepCents == null) return 0; // custom-quoted / no pack
  const reps =
    tenant.billing?.subscribedReps && tenant.billing.subscribedReps > 0
      ? tenant.billing.subscribedReps
      : Math.max(1, tenant.usage?.users ?? 1);
  return perRepCents * reps;
}

// Session cache so every page/component shares one catalog fetch.
let _cache: PackOption[] | null = null;
let _inflight: Promise<PackOption[]> | null = null;

export interface UsePacks {
  packs: PackOption[];
  bySlug: Record<string, PackOption>;
  loaded: boolean;
}

/** Load the package catalog once and resolve any pack slug to its details. */
export function usePacks(): UsePacks {
  const [packs, setPacks] = useState<PackOption[]>(_cache ?? []);
  const [loaded, setLoaded] = useState<boolean>(_cache != null);

  useEffect(() => {
    if (_cache) { setPacks(_cache); setLoaded(true); return; }
    if (!_inflight) _inflight = fetchPackages().then((r) => { _cache = r.packs; return r.packs; });
    let on = true;
    _inflight
      .then((p) => { if (on) { setPacks(p); setLoaded(true); } })
      .catch(() => { if (on) setLoaded(true); });
    return () => { on = false; };
  }, []);

  const bySlug = useMemo(() => {
    const m: Record<string, PackOption> = {};
    for (const p of packs) m[p.pack] = p;
    return m;
  }, [packs]);

  return { packs, bySlug, loaded };
}
